import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import { findOneERequisition, approveRejectERequisition, fetchERequisitionMaster, ERequisitionItem } from '../../../core/data/redux/eRequisitionSlice';
import { getRequestTypeLabel, getItemTypeLabel } from './ERequisitionITList';
import {
  getRequestStatusConfig,
  getRequestStatusLabel,
  SapRequestStatus
} from '../../../core/data/constants/requestStatus';

const ERequisitionITDetails: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const employeesState = useSelector((state: RootState) => state.employeesState);
  const eState = useSelector((state: RootState) => state.eRequisitionState);

  const profile = employeesState?.data?.data?.details || employeesState?.data?.details;
  const loggedInEmpId = String(profile?.empId || profile?.employee_id || '');

  const masterRaw = eState?.masterData?.data?.data || eState?.masterData?.data;
  const itemMasterList = masterRaw?.Item_List_Type || [];
  const formMasterList = masterRaw?.Form_Type || [];

  const [modalAction, setModalAction] = useState<'A' | 'R' | null>(null);
  const [remarks, setRemarks] = useState('');
  const [approverId, setApproverId] = useState('');
  const [allotments, setAllotments] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);

  const loadDetails = () => {
    if (id) dispatch(findOneERequisition({ ReferenceNumber: id }));
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchERequisitionMaster({ page: 1, limit: 20 }));
    loadDetails();
  }, [dispatch, id]);

  const apiPayloadData = eState?.detailData?.data?.data || eState?.detailData?.data?.details || eState?.detailData?.data || eState?.detailData;
  const rawDetail = Array.isArray(apiPayloadData) ? apiPayloadData[0] : apiPayloadData;

  const reqData = rawDetail && typeof rawDetail === 'object' ? rawDetail : {};
  const itemsList: ERequisitionItem[] = reqData?.ToList?.results || reqData?.List_Item?.results || reqData?.results || reqData?.items || [];
  const remarksTrail = reqData?.ToRemarks?.results || reqData?.ToRemarks || [];
  const formType = String(reqData?.SelectRequestFromType || '00').trim();
  const reqStatus = String(reqData?.RequestStatus || '').trim().toUpperCase();
  const currentStatus = String(reqData?.Status || reqData?.RequestStatus || reqData?.requestStatus || '');

  const isFromUniversalWorklist = Boolean(
    location.state?.isApprover ||
    location.state?.fromUniversalWorklist ||
    searchParams.get('isApprover') === 'true' ||
    searchParams.get('fromWorklist') === 'true'
  );

  const isUserApprover = Boolean(
    isFromUniversalWorklist ||
    (loggedInEmpId && (
      String(reqData?.ApproverUserID || '').trim() === loggedInEmpId ||
      String(reqData?.ApproverName || '').trim() === loggedInEmpId
    ) && String(reqData?.EmpID || '').trim() !== loggedInEmpId)
  );

  const hasBeenActedOn =
    reqStatus === SapRequestStatus.APPROVED ||
    reqStatus === SapRequestStatus.REJECTED ||
    currentStatus.toLowerCase() === 'approved' ||
    currentStatus.toLowerCase() === 'rejected' ||
    Boolean(reqData.HRemarks && (reqData.HRemarks.toLowerCase().includes('approved') || reqData.HRemarks.toLowerCase().includes('rejected'))) ||
    remarksTrail.some((r: any) => Boolean(r.HRemarks && (r.HRemarks.toLowerCase().includes('approved') || r.HRemarks.toLowerCase().includes('rejected'))));

  const isPending = !hasBeenActedOn && (
    reqStatus === SapRequestStatus.PARTIALLY_APPROVED ||
    reqStatus === 'W' ||
    reqStatus === SapRequestStatus.TO_BE_APPROVED ||
    reqStatus === SapRequestStatus.TO_BE_PROCESSED ||
    currentStatus.toLowerCase().includes('in-process') ||
    currentStatus.toLowerCase().includes('pending') ||
    currentStatus.toLowerCase().includes('to be approved') ||
    currentStatus.toLowerCase().includes('to be processed') ||
    currentStatus === 'Submitted'
  );

  const canApproveReject = isPending && isUserApprover;

  const rawStatus = reqData?.RequestStatus || reqData?.requestStatus || reqData?.Status || reqData?.status || SapRequestStatus.PARTIALLY_APPROVED;
  const statusConfig = getRequestStatusConfig(rawStatus);
  let displayStatus = statusConfig.label;
  let displayBadgeClass = statusConfig.badgeClass;

  if (hasBeenActedOn) {
    const isRejected = reqStatus === SapRequestStatus.REJECTED || currentStatus.toLowerCase().includes('reject') || reqData.HRemarks?.toLowerCase().includes('rejected');
    displayStatus = isRejected ? 'Rejected' : 'Approved';
    displayBadgeClass = isRejected ? 'badge bg-danger-subtle text-danger border border-danger-subtle' : 'badge bg-success-subtle text-success border border-success-subtle';
  }

  useEffect(() => {
    if (itemsList && itemsList.length > 0) {
      const map: Record<string, string> = {};
      itemsList.forEach((it, i) => {
        const key = it.SerialNumber || String(i + 1).padStart(2, '0');
        map[key] = it.AllotementOrNot || '1';
      });
      setAllotments(map);
    }
  }, [reqData]);

  const openModal = (act: 'A' | 'R') => {
    setModalAction(act);
    setRemarks('');
    setApproverId(loggedInEmpId);
  };

  const handleAllotmentChange = (serialNo: string, val: string) => {
    setAllotments(prev => ({ ...prev, [serialNo]: val }));
  };

  const handleApproveRejectSubmit = async () => {
    if (!remarks.trim()) return toast.error('Remarks are required for submission');
    setIsProcessing(true);

    const payload: any = {
      Action: modalAction,
      ReferenceNumber: reqData.ReferenceNumber || id,
      EmpID: reqData.EmpID || loggedInEmpId,
      SelectRequestFromType: formType,
      HRemarks: remarks.trim(),
      ApproverUserID: approverId.trim() || loggedInEmpId
    };

    if (formType === '00') {
      payload.List_Item = {
        results: itemsList.map((item, idx) => {
          const serial = item.SerialNumber || String(idx + 1).padStart(2, '0');
          return {
            ReferenceNumber: reqData.ReferenceNumber || id,
            SerialNumber: serial,
            AllotementOrNot: allotments[serial] || item.AllotementOrNot || '1'
          };
        })
      };
    }

    try {
      const res = await dispatch(approveRejectERequisition(payload));
      if (approveRejectERequisition.fulfilled.match(res)) {
        toast.success(`Request successfully ${modalAction === 'A' ? 'approved' : 'rejected'}!`);
        setModalAction(null);
        navigate(routes.universalWorklist || '/universal-worklist');
      } else {
        toast.error((res.payload as string) || 'Action submission failed');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error processing action');
    } finally {
      setIsProcessing(false);
    }
  };

  const baseDetailsGrid = [
    { label: 'Employee Name', value: reqData.EmployeeName || reqData.employeeName || reqData.EmpName || '-' },
    { label: 'Employee No.', value: reqData.EmpID || reqData.employeeNo || '-' },
    { label: 'Designation', value: reqData.Designation || reqData.designation || '-' },
    { label: 'Department', value: reqData.Department || reqData.department || '-' },
    { label: 'Mobile No.', value: reqData.ContactNumber || '-' },
    { label: 'Official Email', value: reqData.EmailID || '-' },
    { label: 'Office Location', value: reqData.OfficeLocation || '-' },
    { label: 'Request Form Type', value: reqData.SelectRequestFromTypeText || reqData.requestFormType }
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Top Header */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="page-title mb-0 text-dark fw-bold">IT Requisition Details</h4>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.employeeDashboard || '#'}>Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to={routes.eRequisitionIT.list}>E-Requisition (IT)</Link></li>
                <li className="breadcrumb-item active">Details</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm px-3" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1"></i>Back to List
            </button>
            {canApproveReject && (
              <>
                <button className="btn btn-outline-danger btn-sm px-3" onClick={() => openModal('R')}>
                  <i className="ti ti-x me-1"></i>Reject
                </button>
                <button className="btn btn-success btn-sm px-3" onClick={() => openModal('A')}>
                  <i className="ti ti-check me-1"></i>Approve
                </button>
              </>
            )}
          </div>
        </div>

        {eState.loading === 'pending' ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status"></div></div>
        ) : (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold text-dark"><i className="ti ti-file-text me-2 text-danger"></i>Requisition #{reqData.ReferenceNumber || id}</h5>
              </div>
              <span className={`badge px-3 py-1.5 fs-12 rounded-pill ${displayBadgeClass}`}>
                {displayStatus}
              </span>
            </div>

            <div className="card-body p-4">
              {/* Applicant Profile Information Grid */}
              <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="fw-bold text-dark mb-0 fs-14">
                    <i className="ti ti-user-check me-2 text-danger"></i>Applicant Information
                  </h6>
                  <span className="badge bg-light text-dark border">
                    <i className="ti ti-id me-1 text-muted"></i>{reqData.EmpID || '-'}
                  </span>
                </div>
                <div className="row g-3">
                  {baseDetailsGrid.map((f, idx) => (
                    <div className="col-md-3 col-sm-6" key={idx}>
                      <div className="p-2 rounded bg-white border-subtle h-100">
                        <span className="text-muted small d-block mb-1 fs-12">{f.label}</span>
                        <span className="fw-semibold text-dark fs-13 d-block text-truncate" title={String(f.value)}>{f.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Type "00": Equipment Requisition Items Table */}
              {formType === '00' && (
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-bold text-dark mb-0 fs-14">
                      <i className="ti ti-devices me-2 text-danger"></i>IT Equipment Requisition Form
                    </h6>
                    <span className="badge bg-danger-subtle text-danger px-2.5 py-1 fw-semibold">{itemsList.length} Item(s)</span>
                  </div>

                  <div className="table-responsive border rounded-3 overflow-hidden shadow-2xs">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light border-bottom">
                        <tr>
                          <th className="text-center py-2.5 text-muted small fw-bold" style={{ width: '60px' }}>#</th>
                          <th className="py-2.5 text-muted small fw-bold">Item Code & Name</th>
                          <th className="py-2.5 text-muted small fw-bold">Remarks / Specification</th>
                          <th className="text-center py-2.5 text-muted small fw-bold" style={{ width: '180px' }}>Allotment Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsList.map((it, idx) => {
                          const serial = it.SerialNumber || String(idx + 1).padStart(2, '0');
                          const currentAllot = allotments[serial] || it.AllotementOrNot || '1';
                          const itemLabel = getItemTypeLabel(it.Items, itemMasterList);
                          return (
                            <tr key={idx}>
                              <td className="text-center fw-bold text-secondary fs-13">{serial}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-danger-subtle text-danger me-2 px-2 py-1 fs-12 border border-danger-subtle">{it.Items}</span>
                                  <span className="fw-semibold text-dark fs-13">{itemLabel}</span>
                                </div>
                              </td>
                              <td>
                                <span className="text-muted fs-13">{it.Remarks || '-'}</span>
                              </td>
                              <td className="text-center">
                                {canApproveReject ? (
                                  <select
                                    className="form-select form-select-sm mx-auto shadow-2xs border-primary-subtle text-dark fw-medium"
                                    style={{ maxWidth: '160px' }}
                                    value={currentAllot}
                                    onChange={e => handleAllotmentChange(serial, e.target.value)}
                                  >
                                    <option value="1">Alloted</option>
                                    <option value="2">Not Alloted</option>
                                  </select>
                                ) : (
                                  <span className={`badge px-3 py-1.5 fs-12 border ${currentAllot === '1' ? 'bg-success-subtle text-success border-success-subtle' : 'bg-secondary-subtle text-secondary border-secondary-subtle'}`}>
                                    {currentAllot === '1' ? (
                                      <><i className="ti ti-check me-1"></i>Alloted</>
                                    ) : (
                                      <><i className="ti ti-x me-1"></i>Not Alloted</>
                                    )}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-6">
                      <div className="p-3 border rounded-3 bg-light-subtle d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-shield-check text-primary fs-18"></i>
                          <span className="text-dark small fw-medium">Only for requirement of Supervisors: PCs issued to Supervisors are not more than 50% of Supervisors working under concerned HOD</span>
                        </div>
                        <span className={`badge px-2.5 py-1 ${reqData.SupervisorUnderHOD === '1' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-secondary-subtle text-secondary border'}`}>
                          {reqData.SupervisorUnderHOD === '1' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-3 border rounded-3 bg-light-subtle d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-devices text-warning fs-18"></i>
                          <span className="text-dark small fw-medium">Already Issued IT assets (if any)</span>
                        </div>
                        <span className={`badge px-2.5 py-1 ${reqData.AlreadyIssueITAsset === '1' ? 'bg-warning-subtle text-warning border border-warning-subtle' : 'bg-secondary-subtle text-secondary border'}`}>
                          {reqData.AlreadyIssueITAsset === '1' ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Type "11": e-Office Details */}
              {formType === '11' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-mail me-2 text-danger"></i>e-Office Access Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">Date of Birth</span><span className="fw-semibold text-dark fs-14">{reqData.DateOfBirth || '-'}</span></div>
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">Type of Appointment</span><span className="fw-semibold text-dark fs-14">{reqData.TypeOfAppointment || '-'}</span></div>
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">From date working in current department</span><span className="fw-semibold text-dark fs-14">{reqData.WorkingFromDate || '-'}</span></div>
                  </div>
                </div>
              )}

              {/* Form Type "12": VPP/MPR Details */}
              {formType === '12' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-key me-2 text-danger"></i>VPP & MPR Profile Details</h6>
                  <div className="row g-3">
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">Status</span><span className="fw-semibold text-dark fs-14">{reqData.VPPStatusText || reqData.VPPStatus || '-'}</span></div>
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">Select if you want authorization for VPP</span><span className="fw-semibold text-dark fs-14">{reqData.UserProfileForVPPText || reqData.UserProfileForVPP || '-'}</span></div>
                    <div className="col-md-4 col-6"><span className="text-muted small d-block mb-1">Select if you want authorization for MPR</span><span className="fw-semibold text-dark fs-14">{reqData.UserProfileForMPRText || reqData.UserProfileForMPR || '-'}</span></div>
                  </div>
                </div>
              )}

              {/* Form Type "13": VPN Access Details */}
              {formType === '13' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-shield-lock me-2 text-danger"></i>VPN Network Access Details</h6>
                  <div className="row g-3">
                    <div className="col-md-3 col-6"><span className="text-muted small d-block mb-1">Operating System Name and Version</span><span className="fw-semibold text-dark fs-14">{reqData.OperatingSystemVersion || '-'}</span></div>
                    <div className="col-md-3 col-6"><span className="text-muted small d-block mb-1">Antivirus (Name and Version)</span><span className="fw-semibold text-dark fs-14">{reqData.Antivirus || '-'}</span></div>
                    <div className="col-md-3 col-6"><span className="text-muted small d-block mb-1">System MAC address</span><span className="fw-semibold text-dark fs-14">{reqData.MacAddress || '-'}</span></div>
                    <div className="col-md-3 col-6"><span className="text-muted small d-block mb-1">Firewall status</span><span className="fw-semibold text-dark fs-14">{reqData.FirewallStatusText || reqData.FirewallStatus || '-'}</span></div>
                  </div>
                </div>
              )}

              {/* Form Type "14": Third-Party Access Details */}
              {formType === '14' && (
                <div className="bg-light-subtle rounded-3 p-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-world me-2 text-danger"></i>Third Party E-mail Access</h6>
                  <div className="row g-3">
                    <div className="col-md-6 col-6"><span className="text-muted small d-block mb-1">Select E-mail Service</span><span className="fw-semibold text-dark fs-14">{reqData.ServicesText || reqData.Services || '-'}</span></div>
                    <div className="col-md-6 col-6"><span className="text-muted small d-block mb-1">System IP Address</span><span className="fw-semibold text-dark fs-14">{reqData.SystemIPAddress || '-'}</span></div>
                  </div>
                </div>
              )}

              {/* Remarks & Approval History Trail */}
              {(remarksTrail.length > 0 || reqData.Remarks || reqData.HRemarks) && (
                <div className="p-3 border rounded-3 bg-white">
                  <h6 className="fw-bold text-dark mb-3 fs-14"><i className="ti ti-history me-2 text-danger"></i>Approval History & Remarks</h6>
                  {remarksTrail.length > 0 ? (
                    <div className="table-responsive border rounded-3 overflow-hidden">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="py-2 text-muted small fw-bold" style={{ width: '80px' }}>Level</th>
                            <th className="py-2 text-muted small fw-bold">Approver Name</th>
                            <th className="py-2 text-muted small fw-bold">Timestamp</th>
                            <th className="py-2 text-muted small fw-bold">Remarks / Action Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {remarksTrail.map((rem: any, rIdx: number) => (
                            <tr key={rIdx}>
                              <td><span className="badge bg-light text-dark border">L{rem.ApproverLevel || '1'}</span></td>
                              <td>
                                <span className="fw-semibold text-dark d-block fs-13">{rem.ApproverName || rem.ApproverUserID}</span>
                                <small className="text-muted fs-11">({rem.EmpID || rem.ApproverUserID})</small>
                              </td>
                              <td><small className="text-muted fs-12">{rem.TimeStamp || '-'}</small></td>
                              <td><span className="text-dark small fs-13">{rem.HRemarks || rem.Remarks || '-'}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-3 bg-light-subtle rounded-3 border">
                      <span className="text-muted small d-block mb-1">Remarks</span>
                      <p className="text-dark mb-0 small fw-medium">{reqData.HRemarks || reqData.Remarks || 'No remarks recorded.'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compact Approver Action Modal */}
        {modalAction && (
          <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
              <div className="modal-content border-0 shadow">
                <div className={`modal-header py-2 px-3 ${modalAction === 'A' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                  <h6 className="modal-title fw-bold mb-0 d-flex align-items-center fs-15">
                    <i className={`ti ${modalAction === 'A' ? 'ti-check-circle' : 'ti-circle-x'} me-2 fs-18`}></i>
                    {modalAction === 'A' ? 'Approve IT Requisition' : 'Reject IT Requisition'}
                  </h6>
                  <button type="button" className="btn-close" onClick={() => setModalAction(null)}></button>
                </div>
                <div className="modal-body p-3">
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-dark small mb-1">Approver Employee ID</label>
                    <input type="text" className="form-control form-control-sm" value={approverId} onChange={e => setApproverId(e.target.value)} placeholder="Approver ID" />
                  </div>
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-dark small mb-1">
                      Approver Remarks <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      placeholder={modalAction === 'A' ? 'Enter approval notes...' : 'Enter rejection reason...'}
                      value={remarks}
                      onChange={e => setRemarks(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer bg-light-subtle border-top py-2 px-3 d-flex align-items-center justify-content-end gap-2">
                  <button type="button" className="btn btn-light btn-sm px-3" onClick={() => setModalAction(null)} disabled={isProcessing}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-3 shadow-xs text-white ${modalAction === 'A' ? 'btn-success' : 'btn-danger'}`}
                    onClick={handleApproveRejectSubmit}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Submitting...' : modalAction === 'A' ? 'Confirm Approval' : 'Confirm Rejection'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ERequisitionITDetails;

